import React from 'react';
import { AbstractButton, Box, Button, Flex, Icon, IconButton, Text } from 'pounceV2';
import { useTheme } from '@mui/material';
import { RecommendedAction } from '../enrichedAIResponseUtils';

interface RecommendedActionsV3Props {
  actions: RecommendedAction[];
  onRun: (prompt: string) => void;
  disabled?: boolean;
  /** Optional content rendered inside the card before the first AI-prompt row. */
  header?: React.ReactNode;
}

const cardStyles = {
  mt: 2,
  mb: 4,
  border: '1px solid',
  borderRadius: 'medium',
  overflow: 'hidden',
} as const;

const RecommendedActionsV3: React.FC<RecommendedActionsV3Props> = ({
  actions,
  onRun,
  disabled = false,
  header,
}) => {
  const {
    palette: { tokens },
  } = useTheme();
  const borderColor = tokens.stroke.neutral[4];
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);

  // Reset to list view when actions change (e.g. a new AI response arrives while detail is open).
  React.useEffect(() => {
    setSelectedIndex(null);
  }, [actions]);

  const action = selectedIndex !== null ? (actions[selectedIndex] ?? null) : null;

  // Detail view — action is null if selectedIndex is stale (between effect firing and re-render)
  if (selectedIndex !== null && action !== null) {
    const heading = action.title ?? action.prompt;
    return (
      <Box {...cardStyles} borderColor={borderColor}>
        {/* Back navigation */}
        <Flex
          p={5}
          sx={{ borderBottom: `1px solid ${borderColor}` }}
          backgroundColor={tokens.background.neutral.raised[6]}
        >
          <AbstractButton
            display="flex"
            alignItems="center"
            gap={2}
            color={tokens.content.text.neutral[2]}
            borderRadius="small"
            px={2}
            py={1}
            _hover={{
              color: tokens.content.text.neutral.base,
              backgroundColor: tokens.background.neutral.raised[4],
            }}
            onClick={() => setSelectedIndex(null)}
          >
            <Icon type="chevron-left" size="medium" />
            <Text variant="body/sm" fontWeight="semibold" color="inherit">
              Back to recommended prompts
            </Text>
          </AbstractButton>
        </Flex>

        {/* Content */}
        <Box p={5} backgroundColor={tokens.background.neutral.raised[6]}>
          <Text variant="body/md" fontWeight="bold" color={tokens.content.text.neutral.base}>
            {heading}
          </Text>

          {action.description && (
            <Text variant="body/md" color={tokens.content.text.neutral[2]} mt={1}>
              {action.description}
            </Text>
          )}

          <Text
            variant="body/sm"
            fontWeight="semibold"
            color={tokens.content.text.neutral[2]}
            mt={3}
            mb={1}
          >
            Full Prompt
          </Text>
          <Box
            mb={4}
            p={5}
            borderRadius="small"
            sx={{ border: `1px solid ${borderColor}` }}
            backgroundColor={tokens.background.brand.dark.base}
          >
            <Text variant="body/sm" color={tokens.content.text.neutral[2]}>
              {action.prompt}
            </Text>
          </Box>

          <Flex justify="flex-end">
            <Button
              variant="primary"
              size="small"
              disabled={disabled}
              onClick={() => onRun(action.prompt)}
            >
              Run Prompt
              <Icon type="paper-plane" size="x-small" sx={{ fontSize: '10px', ml: 1 }} />
            </Button>
          </Flex>
        </Box>
      </Box>
    );
  }

  // List view
  return (
    <Box {...cardStyles} borderColor={borderColor}>
      <Box
        p={5}
        sx={{ borderBottom: `1px solid ${borderColor}` }}
        backgroundColor={tokens.background.neutral.raised[6]}
      >
        <Text variant="body/lg" fontWeight="bold" color={tokens.content.text.neutral[2]}>
          Next Steps
        </Text>
      </Box>
      {header}
      {actions.map((listAction, index) => {
        const isLastRow = index === actions.length - 1;
        const displayText = listAction.title ?? listAction.prompt;
        return (
          <Flex
            key={listAction.prompt}
            alignItems="center"
            gap={2}
            p={5}
            backgroundColor={tokens.background.neutral.raised[6]}
            sx={isLastRow ? undefined : { borderBottom: `1px solid ${borderColor}` }}
          >
            {/* Title or prompt fallback — truncated to one line */}
            <Box flex={1} minWidth={0}>
              <Text
                variant="body/md"
                fontWeight="semibold"
                color={tokens.content.text.neutral.base}
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {displayText}
              </Text>
            </Box>

            {/* Run button — executes the prompt immediately */}
            <Button
              variant="outline"
              size="small"
              flexShrink={0}
              disabled={disabled}
              onClick={() => onRun(listAction.prompt)}
            >
              Run
              <Icon type="paper-plane" size="x-small" sx={{ fontSize: '10px', ml: 1 }} />
            </Button>

            {/* Chevron button — navigates to the detail view */}
            <IconButton
              icon="chevron-right"
              aria-label="View prompt details"
              variant="ghost"
              size="small"
              onClick={() => setSelectedIndex(index)}
            />
          </Flex>
        );
      })}
    </Box>
  );
};

export default RecommendedActionsV3;
